import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { MatchesService } from '../../services/matches.service';
import { SidenavService } from '../../services/sidenav.service';
import { StandingsService } from '../../services/standings.service';

interface WorldCupTeam {
  id: number;
  name: string;
  shortName: string;
  tla: string;
  crest: string;
}

interface WorldCupStandingRow {
  position: number;
  team: WorldCupTeam;
  playedGames: number;
  won: number;
  draw: number;
  lost: number;
  points: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
}

interface WorldCupGroupStanding {
  stage: string;
  type: string;
  group: string;
  table: WorldCupStandingRow[];
}

@Component({
  selector: 'app-stats-group-standings',
  templateUrl: './stats-group-standings.component.html',
  styleUrls: ['./stats-group-standings.component.scss'],
  standalone: false,
})
export class StatsGroupStandingsComponent implements OnInit, OnDestroy {
  sidenav: MatSidenav;
  standings: WorldCupGroupStanding[] = [];
  loading = true;
  loadFailed = false;

  private liveTeamIds = new Set<string>();
  private liveMatchesInterval?: ReturnType<typeof setInterval>;

  constructor(
    private sidenavService: SidenavService,
    private standingsService: StandingsService,
    private matchesService: MatchesService,
    private router: Router,
  ) {
    this.sidenav = this.sidenavService.getSidenav();
  }

  async ngOnInit() {
    await this.loadData();
    this.liveMatchesInterval = setInterval(
      () => void this.refreshLiveMatches(),
      60000,
    );
  }

  ngOnDestroy(): void {
    if (this.liveMatchesInterval) {
      clearInterval(this.liveMatchesInterval);
    }
  }

  goBack() {
    this.router.navigate(['/stats']);
  }

  async retry() {
    await this.loadData();
  }

  getGroupName(group: string): string {
    return group.replace(/^Group\s+/i, 'Grupo ');
  }

  getGoalDifference(row: WorldCupStandingRow): string {
    return row.goalDifference > 0
      ? `+${row.goalDifference}`
      : `${row.goalDifference}`;
  }

  isTeamLive(teamId: number): boolean {
    return this.liveTeamIds.has(String(teamId));
  }

  isGroupLive(standing: WorldCupGroupStanding): boolean {
    return standing.table.some((row) => this.isTeamLive(row.team.id));
  }

  private async loadData() {
    this.loading = true;
    this.loadFailed = false;

    try {
      const [standingsResponse, matches] = await Promise.all([
        this.standingsService.getWorldCupGroupStandings(),
        this.matchesService.getList(),
      ]);
      this.standings = standingsResponse?.standings || [];
      this.updateLiveTeams(matches);
    } catch {
      this.standings = [];
      this.liveTeamIds.clear();
      this.loadFailed = true;
    } finally {
      this.loading = false;
    }
  }

  private async refreshLiveMatches(): Promise<void> {
    try {
      const matches = await this.matchesService.getList();
      this.updateLiveTeams(matches);
    } catch {
      // Keep the latest known state until the next refresh succeeds.
    }
  }

  private updateLiveTeams(matches: any[]): void {
    const liveMatches = (matches || []).filter((match: any) =>
      ['IN_PLAY', 'LIVE', 'PAUSED'].includes(match.status),
    );
    const teamIds = liveMatches.flatMap((match: any) => [
      match.homeTeam?.externalId,
      match.awayTeam?.externalId,
    ]);

    this.liveTeamIds = new Set(
      teamIds.filter((teamId: unknown) => teamId != null).map(String),
    );
  }
}
